/**
 * Friends data — the sources the Tonight feed (`/`, features/tonight) merges
 * client-side (docs/api-schema.md §Friends: "clients merge the activity and
 * following feeds"). The old `/friends` screen is gone (it redirects to `/`);
 * these hooks are its data layer, kept and extended:
 *
 * - `['friends','activity']` → `GET /v1/friends/activity` — watch events from
 *   actively followed users, newest first. Rows carry the additive `rating`
 *   (1–5, omitted when unrated / on older backends) — the shared domain
 *   mapper drops it, so it is re-attached here as `FriendActivityItem.rating`.
 * - `['friends','following']` → `GET /v1/community/feed/following` — recent
 *   comments from active friends (T1; also consumed by the Discussions hub).
 * - `['friends','trending']` → `GET /v1/community/feed/trending` — top-voted
 *   comments on the caller's library titles, from anyone; `locale` required.
 * - `['friends','watching', n]` → `GET /v1/friends/watching` — who is watching
 *   what right now (the Tonight "Watching now" strip).
 *
 * All mirror the mobile client's `404 → empty` degrade for an unshipped
 * endpoint.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { apiGet, ApiError } from '@/api/http';
import { getLocale, useLocale } from '@/i18n/locale';

import type {
  ApiCommentAuthor,
  ApiCommunityFeedPage,
  ApiFriendActivityItem,
  ApiFriendsActivityPage,
} from '@shared/api-types';
import {
  imageUrls,
  mapCommentAuthor,
  mapCommunityFeedItem,
  mapFriendActivityItem,
  paletteFromTitle,
} from '@shared/mappers';
import type {
  CommentAuthor,
  CommunityFeedItem,
  CommunityFeedPage,
  CommunityLocale,
  UserActivityItem,
} from '@shared/types';

/**
 * A `/v1/friends/activity` row with the additive per-event `rating` (1–5).
 * Absent on unrated events, on `rewatch` rows, and on backends that predate
 * the field — always treat absent as a plain watch.
 */
export interface FriendActivityItem extends UserActivityItem {
  rating?: number;
}

export interface FriendActivityPage {
  items: FriendActivityItem[];
  cursor?: string;
}

/** Keep only a valid 1–5 integer rating; anything else is "no rating". */
export function validRating(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  const rounded = Math.round(value);
  return rounded >= 1 && rounded <= 5 ? rounded : undefined;
}

export function mapFriendActivity(item: ApiFriendActivityItem): FriendActivityItem {
  const mapped: FriendActivityItem = mapFriendActivityItem(item);
  const rating = validRating((item as { rating?: unknown }).rating);
  if (rating !== undefined) mapped.rating = rating;
  return mapped;
}

export const FRIENDS_ACTIVITY_KEY = ['friends', 'activity'] as const;
export const FRIENDS_TRENDING_KEY = ['friends', 'trending'] as const;
export const FRIENDS_WATCHING_KEY = ['friends', 'watching'] as const;
export const FRIENDS_FOLLOWING_KEY = ['friends', 'following'] as const;

const PER_PAGE = 20;

/**
 * The trending feed requires a community locale; web derives it from the app's
 * locale setting (getLocale — the module-level mirror <LocaleProvider> keeps
 * in sync), not from the browser. `Locale` and `CommunityLocale` share the
 * same 'en' | 'pt-BR' values, so the mapping is identity.
 */
export function communityLocale(): CommunityLocale {
  return getLocale();
}

export async function fetchActivityPage(cursor?: string): Promise<FriendActivityPage> {
  const query: Record<string, string | number | undefined> = { per_page: PER_PAGE };
  if (cursor) query.cursor = cursor;
  try {
    const r = await apiGet<ApiFriendsActivityPage>('/v1/friends/activity', { query });
    return {
      items: (r.items ?? [])
        .filter((i): i is NonNullable<typeof i> => Boolean(i))
        .map(mapFriendActivity),
      cursor: r.cursor ?? undefined,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { items: [] };
    throw err;
  }
}

export async function fetchTrendingPage(
  locale: CommunityLocale,
  cursor?: string,
): Promise<CommunityFeedPage> {
  const query: Record<string, string | number | undefined> = { locale, per_page: PER_PAGE };
  if (cursor) query.cursor = cursor;
  try {
    const r = await apiGet<ApiCommunityFeedPage>('/v1/community/feed/trending', { query });
    return {
      items: (r.items ?? [])
        .filter((i): i is NonNullable<typeof i> => Boolean(i))
        .map(mapCommunityFeedItem),
      cursor: r.cursor ?? undefined,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { items: [] };
    throw err;
  }
}

/** From friends — watch events, cursor-paginated. */
export function useFriendsActivity() {
  return useInfiniteQuery({
    queryKey: FRIENDS_ACTIVITY_KEY,
    queryFn: ({ pageParam }) => fetchActivityPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor,
  });
}

/** Trending — popular comments on library titles, cursor-paginated. The
 *  locale is part of the query key, so switching the app language refetches
 *  the pt-BR (or English) partition. */
export function useTrendingFeed() {
  const { locale } = useLocale();
  return useInfiniteQuery({
    queryKey: [...FRIENDS_TRENDING_KEY, locale],
    queryFn: ({ pageParam }) => fetchTrendingPage(locale, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor,
  });
}

/** Flatten an infinite query's pages into one newest-first item list,
 *  deduped by event id (a retried/refetched page can overlap). */
export function flattenActivityPages(
  data: ReturnType<typeof useFriendsActivity>['data'],
): FriendActivityItem[] {
  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

// --- following feed (friends' comments) ------------------------------------------

/**
 * `GET /v1/community/feed/following?per_page=&cursor=` — recent visible
 * comments from active friends, newest first, cursor over the caller's
 * connection edges. `404` (older backend) → empty.
 */
export async function fetchFollowingPage(cursor?: string): Promise<CommunityFeedPage> {
  const query: Record<string, string | number | undefined> = { per_page: PER_PAGE };
  if (cursor) query.cursor = cursor;
  try {
    const r = await apiGet<ApiCommunityFeedPage>('/v1/community/feed/following', { query });
    return {
      items: (r.items ?? [])
        .filter((i): i is NonNullable<typeof i> => Boolean(i))
        .map(mapCommunityFeedItem),
      cursor: r.cursor ?? undefined,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { items: [] };
    throw err;
  }
}

/** Friends' comments, cursor-paginated (Tonight feed, Discussions "Friends"). */
export function useFollowingFeed() {
  return useInfiniteQuery({
    queryKey: FRIENDS_FOLLOWING_KEY,
    queryFn: ({ pageParam }) => fetchFollowingPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor,
  });
}

/** Flatten + dedupe following pages by `comment_id`. */
export function flattenFollowingPages(
  data: ReturnType<typeof useFollowingFeed>['data'],
): CommunityFeedItem[] {
  return dedupeFeedItems(data?.pages.flatMap((p) => p.items) ?? []);
}

function dedupeFeedItems(items: CommunityFeedItem[]): CommunityFeedItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const id = item.comment.commentId;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** Flatten + dedupe trending pages (clients dedupe `comment_id` across pages). */
export function flattenTrendingPages(
  data: ReturnType<typeof useTrendingFeed>['data'],
): CommunityFeedItem[] {
  return dedupeFeedItems(data?.pages.flatMap((p) => p.items) ?? []);
}

/**
 * `GET /v1/friends/watching?per_page=1` — a one-row probe so the from-friends
 * empty state can offer the people-search CTA when the caller follows no one
 * with anything currently watching. Not in `@shared/api-types` yet (no mobile
 * client method), so the wire shape stays local and minimal.
 */
export async function fetchHasWatchingFriends(): Promise<boolean> {
  try {
    const r = await apiGet<{ items?: unknown[] }>('/v1/friends/watching', {
      query: { per_page: 1 },
    });
    return (r.items ?? []).length > 0;
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 403)) return false;
    throw err;
  }
}

export function useWatchingFriendsProbe(enabled: boolean) {
  return useQuery({
    queryKey: FRIENDS_WATCHING_KEY,
    queryFn: fetchHasWatchingFriends,
    enabled,
    retry: false,
  });
}

// --- watching now ------------------------------------------------------------------

/** One friend's current watching title (`GET /v1/friends/watching`). */
export interface WatchingFriend {
  friend: CommentAuthor;
  title: {
    id: string;
    title: string;
    titleBr?: string;
    type: 'show' | 'movie';
    posterUrl?: string;
    posterFallbackUrl?: string;
    palette: [string, string];
  };
  updatedAt: string;
}

/**
 * Wire shape of a `/v1/friends/watching` row. Not in `@shared/api-types` (no
 * mobile client method), so it stays local and minimal.
 */
interface ApiWatchingItem {
  friend?: ApiCommentAuthor | null;
  title?: {
    tmdb_id?: string | number | null;
    title?: string | null;
    title_br?: string | null;
    title_type?: string | null;
    poster_path?: string | null;
    poster_key?: string | null;
  } | null;
  updated_at?: string | null;
}

export function mapWatchingItem(row: ApiWatchingItem): WatchingFriend | null {
  if (!row.friend || !row.title || row.title.tmdb_id == null) return null;
  const name = row.title.title ?? '';
  // w185 — these render as ~36px tiles; the mirrored key wins when present.
  const poster = imageUrls(row.title.poster_key, row.title.poster_path, 'w185');
  return {
    friend: mapCommentAuthor(row.friend),
    title: {
      id: String(row.title.tmdb_id),
      title: name,
      titleBr: row.title.title_br ?? undefined,
      type: row.title.title_type === 'movie' ? 'movie' : 'show',
      posterUrl: poster.primary,
      posterFallbackUrl: poster.fallback,
      palette: paletteFromTitle(name),
    },
    updatedAt: row.updated_at ?? '',
  };
}

export async function fetchWatchingFriends(limit: number): Promise<WatchingFriend[]> {
  try {
    const r = await apiGet<{ items?: (ApiWatchingItem | null)[] }>('/v1/friends/watching', {
      query: { per_page: limit },
    });
    return (r.items ?? [])
      .map((row) => (row ? mapWatchingItem(row) : null))
      .filter((row): row is WatchingFriend => row !== null)
      .slice(0, limit);
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 403)) return [];
    throw err;
  }
}

/** "Watching now" — up to `limit` friends and their current title. */
export function useWatchingFriends(limit = 8) {
  return useQuery({
    queryKey: [...FRIENDS_WATCHING_KEY, 'list', limit],
    queryFn: () => fetchWatchingFriends(limit),
  });
}
